import React, { createContext, useContext, useState, useEffect } from 'react';

export interface SettingsContextType {
  audioEnabled: boolean;
  setAudioEnabled: (enabled: boolean) => void;
  shouldRemoveParticipant: boolean;
  setShouldRemoveParticipant: (shouldRemove: boolean) => void;
  showAdultSpinners: boolean;
  toggleAdultSpinners: () => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const [audioEnabled, setAudioEnabledState] = useState(true);
  const [shouldRemoveParticipant, setShouldRemoveParticipantState] = useState(false);
  const [showAdultSpinners, setShowAdultSpinners] = useState(false);

  useEffect(() => {
    const savedAudioEnabled = localStorage.getItem('audioEnabled');
    if (savedAudioEnabled !== null) {
      setAudioEnabledState(savedAudioEnabled === 'true');
    }

    const savedShouldRemove = localStorage.getItem('shouldRemoveParticipant');
    if (savedShouldRemove !== null) {
      setShouldRemoveParticipantState(savedShouldRemove === 'true');
    }

    const savedShowAdultSpinners = localStorage.getItem('showAdultSpinners');
    if (savedShowAdultSpinners !== null) {
      setShowAdultSpinners(savedShowAdultSpinners === 'true');
    }
  }, []);

  const setAudioEnabled = (enabled: boolean) => {
    setAudioEnabledState(enabled);
    localStorage.setItem('audioEnabled', String(enabled));
  };

  const setShouldRemoveParticipant = (shouldRemove: boolean) => {
    setShouldRemoveParticipantState(shouldRemove);
    localStorage.setItem('shouldRemoveParticipant', String(shouldRemove));
  };

  const toggleAdultSpinners = () => {
    setShowAdultSpinners(prevState => {
      const newState = !prevState;
      localStorage.setItem('showAdultSpinners', String(newState));
      return newState;
    });
  };

  return (
    <SettingsContext.Provider
      value={{
        audioEnabled,
        setAudioEnabled,
        shouldRemoveParticipant,
        setShouldRemoveParticipant,
        showAdultSpinners,
        toggleAdultSpinners,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) throw new Error('useSettings must be used within a SettingsProvider');
  return context;
}; 